import { ApexOptions } from "apexcharts";
import React, { forwardRef, useEffect, useImperativeHandle } from "react";
import ReactApexChart from "react-apexcharts";
import { Button, List, Modal, Notification } from "rsuite";
import axios from "../../../../utilities/axios";

interface PieChartPerPeriodWeekProps {
    selectedStore: number;
    type: string;
    violationDateRange: string[];
    author: number[];
    associatedOrder: string;
    responsibleEmployee: number[]; 
    errorType: number[];
}

interface PieChartPerPeriodWeekState {
    options: ApexOptions;
    series: number[];
    isOpenModal: boolean;
    modalTitle: string;
    buttonLoading: boolean;
    modalData: any[];
    fullData: any[];
}

export const PieChartPerPeriodWeek = forwardRef((props: PieChartPerPeriodWeekProps, ref) => {
    const [state, setState] = React.useState<PieChartPerPeriodWeekState>({
        series: [],
        options: {
            chart: {
                width: '100%',
                height: '100%',
                type: 'pie',
                toolbar: {
                    show: true,
                    tools: {
                        download: true,
                    },
                },
            },
            grid: {
                padding: {
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                },
            },
            labels: [],
            dataLabels: {
                //@ts-ignore
                formatter(val: any, opts: any) {
                    const name = opts.w.globals.labels[opts.seriesIndex];
                    return [name + "\n" + val.toFixed(1) + '%']
                },
            },
            responsive: [
                {
                    breakpoint: 480,
                    options: {
                        chart: {
                            width: 200,
                        },
                        legend: {
                            position: 'bottom',
                        },
                    },
                },
            ],
            legend: {
                show: false,
            },
        },
        fullData: [],
        modalData: [],
        modalTitle: "",
        isOpenModal: false,
        buttonLoading: false,
    });

    const propsRef = React.useRef(props);
    propsRef.current = props;

    const fullDataRef = React.useRef<any[]>([]);
    const labelsRef = React.useRef<any[]>([]);
    
        useEffect(() => {

            const loadData = async () => {
                await handleGetChartData();
            };
            loadData();
        }, []);
        
        useImperativeHandle(ref, () => ({
            handleGetChartData,
        }));
        
        const handleGetChartData = async () => {
            setState((prev) => ({ ...prev, buttonLoading: true }));
            
            try {
                const { selectedStore, type, violationDateRange, author, associatedOrder, responsibleEmployee, errorType } = propsRef.current;

                 if (!violationDateRange || violationDateRange.length < 2) {
                    setState((prev) => ({ ...prev, buttonLoading: false }));
                    return;
                }
            
                if (!selectedStore) {
                    setState((prev) => ({ ...prev, buttonLoading: false }));
                    return;
                }

                const [startDate, endDate] = violationDateRange.map((date) => new Date(date).toISOString());
                const response = await axios.get("/api/v1/dashboard/get_store_error_chart_data", {
                    params: { selectedStore, type, startDate, endDate, author, associatedOrder, responsibleEmployee, errorType },
                });

             if (response.data?.data) {

                fullDataRef.current = response.data.storeErrorData;
                labelsRef.current = response.data.categories;
                
                setState((prev) => ({
                    ...prev,
                    series: response.data.series,
                    options: { ...prev.options, labels: response.data.categories },
                    fullData: response.data.storeErrorData,
                }));
            } else {
                setState((prev) => ({ ...prev, series: [], options: { ...prev.options, labels: [] } }));
            }
        } catch (error) {
            Notification.error({
                title: "Erro!",
                description: "Erro ao buscar informações do gráfico.",
            });
        }

        setState((prev) => ({ ...prev, buttonLoading: false }));
    };

    const handleOpenModal = (data: any[], title: string) => {
        setState((prev) => ({
            ...prev,
            isOpenModal: true,
            modalTitle: title,
            modalData: data,
        }));
    };

    const handleCloseModal = () => {
        setState((prev) => ({ ...prev, isOpenModal: false }));
    };

    const handleDataPointSelection = (_: any, __: any, config: any) => {
    const index = config.dataPointIndex;

    const label = (state.options.labels?.[index] || "")
        .toString()
        .trim()
        .toUpperCase();

    const periods = ["MANHÃ", "TARDE", "NOITE"];

    const columnName = periods.includes(label)
        ? "infringement_control_period"
        : "infringement_control_day_of_the_week";

    const filteredData = state.fullData.filter((item: any) =>
        (item[columnName] || "")
            .toString()
            .trim()
            .toUpperCase() === label
    );

    const summarizedData = filteredData.reduce((acc: any, item: any) => {
        const key = `${item.user_name} - ${item.error_name}`;
        acc[key] = (acc[key] || 0) + 1;
        return acc;
    }, {});

    const formattedData = Object.entries(summarizedData).map(
        ([key, count]) => `${key}: ${count} pedidos`
    );

    handleOpenModal(formattedData, `Detalhes para ${label}`);
};


    useEffect(() => {
        setState((prev) => ({
            ...prev,
            options: {
                ...prev.options,
                chart: {
                    ...prev.options.chart,
                    events: {
                        dataPointSelection: handleDataPointSelection,
                    },
                },
            },
        }));
    }, [state.fullData]);

    return (
        <>
            <ReactApexChart options={state.options} series={state.series} type="pie" height={500} />

            <Modal backdrop={true} show={state.isOpenModal} onHide={handleCloseModal}>
                <Modal.Header>
                    <Modal.Title>{state.modalTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <List>
                        {state.modalData.map((item: any, index: number) => (
                            <List.Item key={index} index={index}>
                                {item}
                            </List.Item>
                        ))}
                    </List>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={handleCloseModal} appearance="subtle">
                        Fechar
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
});